import { useMediaQuery } from 'usehooks-ts';
import { createContext, memo, useContext, useEffect, useState, type ReactNode } from 'react';
import { sortBy } from 'remeda';

import { Country, Event } from './types';
import { Flag, suggestionFormUrl } from './common';
import { format, formatISOWithOptions } from 'date-fns/fp';
import type { Venue } from '@/common/output';

export const EventCard = memo(function EventCard({ event }: { event: Event }) {
	const { setModalEvent } = useContext(EventModalContext);
	const cantHover = useMediaQuery('(hover: none)');
	const narrowView = useMediaQuery('(max-width: 39rem)');
	const onTapEvent = (cantHover || narrowView) ? () => setModalEvent(event) : undefined;

	return (
		<div key={event.name} onClick={onTapEvent}>
			<article className="event" data-cancelled={event.cancelled ? true : null}>
				<Caption name={event.name} country={event.country} />
				<Cover event={event} />
				<EventSections event={event} />
			</article>
		</div>
	);
});

export const EventModalContext = createContext<{
	modalEvent: Event | null,
	setModalEvent: (event: Event | null) => void,
}>(undefined!);

export const EventModalContextProvider = ({ children }: { children: React.ReactNode }) => {
	const [modalEvent, setModalEvent] = useState<Event | null>(null);

	const set = (event: Event | null) => {
		if (event) { window.history.pushState({ event }, ''); }
		setModalEvent(event);
	};

	useEffect(() => {
		const listener = () => { setModalEvent(null); };
		window.addEventListener('popstate', listener);
		return () => window.removeEventListener('popstate', listener);
	}, [/* once */]);

	return (
		<EventModalContext.Provider value={{ modalEvent, setModalEvent: set }}>
			{children}
		</EventModalContext.Provider>
	);
};

export const EventModal = () => {
	const { modalEvent: event, setModalEvent } = useContext(EventModalContext);

	if (!event) { return null; }

	const onClose = () => setModalEvent(null);

	return <>
		<div className="backdrop" onClick={onClose} />
		<article className="dialog event">
			<header className="caption">
				<Flag country={event.country} />
				<span className="name">{event.name}</span>
				<button onClick={onClose}>&times;</button>
			</header>
			<Cover event={event} />
			<EventSections event={event} />
		</article>
	</>;
};

function RegistrationStatus({ event }: { event: Event }) {
	if (!event.registration || event.cancelled) return null;

	let content =
		(event.registration.status === 'pending') ? <>Registration&nbsp;opens&nbsp;at {event.registration.opens_at}</> :
		(event.registration.status === 'open') ? <>Registration&nbsp;open</> :
		(event.registration.status === 'sold_out') ? <>Event&nbsp;sold&nbsp;out</> :
		null;

	if (!content) return null;

	const url = event.registration.link;

	if (url) content = <a href={url} target="_blank">{content}</a>;

	return (
		<section className="registration">
			{content}
		</section>
	);
}

function EventSections({ event }: { event: Event }) {
	const videos = event.links.filter(({ kind }) => kind === 'videos');

	return <>
		<RegistrationStatus event={event} />

		{Object.keys(event.facebook).map((fb_id, i) => (
			<FacebookLink key={`facebook-${i}`} fbId={fb_id} info={event.facebook[fb_id]} />
		))}

		{(event.ydb ?? []).map((ydb_info, i) => (
			<YdbLink key={`ydb-${i}`} event={event} info={ydb_info} />
		))}

		{(event.westiehub ?? []).map((wh_info, i) => (
			<WestieHubLink key={`westiehub-${i}`} event={event} info={wh_info} />
		))}

		{(event.dancecircuit ?? []).map((dc_info, i) => (
			<DanceCircuitLink key={`dancecircuit-${i}`} event={event} info={dc_info} />
		))}

		{event.links.filter(({ kind }) => kind === 'results').map(({ kind, url }, i) => (
			<SingleLink key={`link-${kind}-${i}`} code={kind} title="Results" url={url} />
		))}

		{videos.length > 1 ? (
			<section key="videos" className="link">
				<b>Videos:</b>
				<ol>
					{videos.map(({ url, title }, i) => (
						<li key={i}><a href={url} title={url} target="_blank">{title ?? url}</a></li>
					))}
				</ol>
			</section>
		) : videos.map(({ kind, url }) => (
			<SingleLink key="videos" code={kind} title="Videos" url={url} />
		))}

		{(event.people && !!event.people.length) && (
			<People people={event.people} />
		)}

		{sortBy(Object.keys(event.venues), venueOrder).map((key: string) => (
			<Location key={key} venue={event.venues[key]} />
		))}

		{event.links.filter(({ kind }) => !isSpecialLinkKey(kind)).map(({ kind, url }, i) => (
			<SingleLink key={`link-${kind}-${i}`} code={kind} title="Website" url={url} />
		))}

		<section className="suggest">
			<a href={suggestFormLink(event)} target="_blank">Suggest a correction for this event</a>
		</section>

		{!!eventTags(event).length && (
			<Tags tags={eventTags(event)} />
		)}
	</>;
}

const People = ({ people }: { people: string[] }) => (
	<section className="people">
		<ul>
			{people.map(name => (
				<li key={name}>{name}</li>
			))}
		</ul>
	</section>
);

const Caption = ({ country, name }: { country: Country, name: string }) => (
	<header className="caption">
		<Flag country={country} />
		<span className="name">{name}</span>
	</header>
);

const Cover = ({ event: { cover, sunday } }: { event: Event }) => {
	if (!cover) return (
		<section className="image"></section>
	);

	const hash = cover.match(/-([0-9a-f]+)\.\w+$/)?.[1];
	if (!hash) return (
		<section className="image"></section>
	);

	const url = `/covers/${format('yyyy/MM/dd', sunday)}/${hash}.webp`;

	return (
		<section className="image">
			<img src={url} onError={({ currentTarget }) => {
				currentTarget.onerror = null;
				currentTarget.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
			}} />
		</section>
	);
};

const TAG_TEXT: { [key: string]: string } = {
	'sold-out': 'sold out',
};

const Tags = ({ tags }: { tags: string[] }) => (
	<section className="tags">
		<ul>
			{tags.map((tag) => (
				<li key={tag} className={tag}>{TAG_TEXT[tag] ?? tag}</li>
			))}
		</ul>
	</section>
);

function suggestFormLink(event: Event): string {
	const sunday = formatISOWithOptions({ representation: 'date' }, event.sunday);
	return suggestionFormUrl + '?usp=pp_url&entry.1103812507=' + encodeURIComponent(sunday + ' ' + event.name);
}

function venueOrder(key: string): string {
	const index = ['fb2', 'wsdc', 'facebook2', 'fb2-raw', 'facebook'].indexOf(key);
	return index >= 0 ? `__${index}` : key
}

function isSpecialLinkKey(key: string): boolean {
	return key.startsWith('facebook:event') || key === 'results' || key === 'videos';
}

function eventTags(event: Event): string[] {
	const { links, hotel, wsdc, cancelled, registration } = event;
	const tags = [];
	if (cancelled) {
		tags.push('cancelled');
	}
	if (registration?.status === 'sold_out' && !cancelled) {
		tags.push('sold-out');
	}
	if (links && links.some(({ kind }) => kind === 'videos')) {
		tags.push('videos');
	}
	if (links && links.some(({ kind }) => kind === 'results')) {
		tags.push('results');
	}
	if (hotel) {
		tags.push('hotel');
	}
	if (wsdc) {
		tags.push('wsdc');
	}
	return tags;
}

const FacebookLink = ({ fbId, info }: { fbId: string, info: { going?: number, responded?: number } }) => {
	const text =
		(info.going && info.responded) ? `${info.going} going, ${info.responded} responded` :
		(info.going) ? `${info.going} going` :
		(info.responded) ? `${info.responded} responded` :
		null;

	return (
		<section className="link facebook">
			<img src="/facebook.png" alt="Facebook logo" className={text ? 'two-lines' : 'one-line'} />
			<span>
				<b><a href={`http://fb.com/events/${fbId}`} target="_blank">Facebook</a></b>
				{text && <><br /> {text}</>}
			</span>
		</section>
	);
}

const YdbLink = ({ event, info }: { event: Event, info: { url: string, going?: number, experiences?: number } }) => {
	const parts = [];
	if (info.going !== undefined && info.going > 0) {
		if (event.sunday > new Date()) {
			parts.push(`${info.going} going`);
		} else {
			parts.push(`${info.going} went`);
		}
	}
	if (info.experiences !== undefined && info.experiences > 0) {
		if (info.experiences === 1) {
			parts.push(`1 experience`);
		} else {
			parts.push(`${info.experiences} experiences`);
		}
	}

	return (
		<section className="link ydb">
			<img src="/ydb.png" alt="YourDanceBuddy logo" className={parts.length ? 'two-lines' : 'one-line'} />
			<span>
				<b><a href={info.url} target="_blank">YourDanceBuddy</a></b>
				{parts.length > 0 && <><br /> {parts.join(', ')}</>}
			</span>
		</section>
	);
}

const WestieHubLink = ({ event, info }: { event: Event, info: { slug: string, attending?: number } }) => {
	const text = info.attending ? (
		event.sunday > new Date() ? `${info.attending} attending` : `${info.attending} attended`
	) : null;

	return (
		<section className="link westiehub">
			<img src="/westiehub.svg" alt="WestieHub logo" className={text ? 'two-lines' : 'one-line'} />
			<span>
				<b><a href={`https://westiehub.dance/events/${info.slug}`} target="_blank">WestieHub</a></b>
				{text && <><br /> {text}</>}
			</span>
		</section>
	);
}

const DanceCircuitLink = ({ event, info }: { event: Event, info: { url: string, going?: number } }) => {
	const text = info.going ? (
		event.sunday > new Date() ? `${info.going} going` : `${info.going} went`
	) : null;

	return (
		<section className="link dancecircuit">
			<img src="/dancecircuit.svg" alt="DanceCircuit logo" className={text ? 'two-lines' : 'one-line'} />
			<span>
				<b><a href={info.url} target="_blank">DanceCircuit</a></b>
				{text && <><br /> {text}</>}
			</span>
		</section>
	);
}

const SingleLink = ({ title, url }: { code: string, title: string, url: string }) => (
	<section className="link">
		<b>{title}:</b> <a href={url} title={url} target="_blank">{url}</a>
	</section>
);

const Location = ({ venue: { name, address, url } }: { venue: Venue }) => {
	const tokens: Array<ReactNode> = [name, address].filter(t => t !== undefined);
	if (!tokens.length) { return null; }
	if (url) { tokens[0] = <a href={url} target="_blank">{tokens[0]}</a>; }

	return (
		<section className="location">
			<b>Location: </b>
			{tokens[0]}
			{(tokens.length > 1) && (
				<>, {tokens[1]}</>
			)}
		</section>
	);
};
